import React, { useEffect } from 'react';
import { StatusDot } from '../../components/ui';
import { channelsApi, teamsApi, agentsApi } from '../../api/client';
import { useAppContext } from '../../store/AppContext';
import type { Channel, Team } from '../../api/types';

interface Props {
  projectId: string;
  activeChannelId: string | null;
  onSelectChannel: (id: string) => void;
  onViewTeams: () => void;
  onViewTasks: () => void;
  onViewSettings: () => void;
  activeView: string;
}

const CHANNEL_TYPE_ICON: Record<string, string> = {
  observable: '👁️',
  private: '🔒',
  standard: '🔓',
};

export const ChannelSidebar: React.FC<Props> = ({
  projectId,
  activeChannelId,
  onSelectChannel,
  onViewTeams,
  onViewTasks,
  onViewSettings,
  activeView,
}) => {
  const { channels, setChannels, teams, setTeams, agents, setAgents, projects, activeProjectId } =
    useAppContext();

  useEffect(() => {
    if (!projectId) return;
    channelsApi.list(projectId).then((res) => { if (res.ok) setChannels(Array.isArray(res.data) ? res.data : ((res.data as { items?: Channel[] }).items ?? [])); });
    teamsApi.list(projectId).then((res) => { if (res.ok) setTeams(Array.isArray(res.data) ? res.data : ((res.data as { items?: Team[] }).items ?? [])); });
    agentsApi.list(projectId).then((res) => { if (res.ok) setAgents(Array.isArray(res.data) ? res.data : ((res.data as { items?: unknown[] }).items as typeof res.data ?? [])); });
  }, [projectId, setChannels, setTeams, setAgents]);

  const project = projects.find((p) => p.id === activeProjectId);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px 10px',
        fontWeight: 700,
        fontSize: 15,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <span>{project?.name ?? 'Project'}</span>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
        {/* Nav items */}
        <SidebarNavItem
          icon="📋"
          label="Tasks"
          active={activeView === 'tasks'}
          onClick={onViewTasks}
        />
        <SidebarNavItem
          icon="👥"
          label="Teams & Agents"
          active={activeView === 'teams'}
          onClick={onViewTeams}
        />

        {/* Channels section */}
        <SectionLabel>Channels</SectionLabel>
        {channels.length === 0 && (
          <div style={{ padding: '4px 16px', fontSize: 12, color: 'var(--text-muted)' }}>
            No channels
          </div>
        )}
        {channels.map((ch: Channel) => {
          const isActive = activeChannelId === ch.id && activeView === 'channel';
          const typeIcon = CHANNEL_TYPE_ICON[ch.type ?? 'standard'] ?? '🔓';
          return (
            <button
              key={ch.id}
              onClick={() => onSelectChannel(ch.id)}
              style={{
                width: 'calc(100% - 12px)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 16px',
                background: isActive ? 'white' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: isActive ? 'var(--text)' : 'var(--text-secondary)',
                fontWeight: isActive ? 500 : 400,
                fontSize: 13,
                borderRadius: 6,
                margin: '1px 6px',
                boxShadow: isActive ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.12s',
              }}
              onMouseEnter={(e) => {
                if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.05)';
              }}
              onMouseLeave={(e) => {
                if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              <span style={{ fontSize: 12, opacity: 0.7 }}>{typeIcon}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 15, lineHeight: 1 }}>#</span>
              <span style={{ flex: 1 }}>{ch.name}</span>
            </button>
          );
        })}

        {/* Teams section */}
        {teams.length > 0 && (
          <>
            <SectionLabel>Teams</SectionLabel>
            {teams.map((t: Team) => {
              const teamAgents = agents.filter((a) => a.teamId === t.id);
              return (
                <div
                  key={t.id}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    borderRadius: 6,
                    margin: '1px 6px',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,0,0,0.05)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                  }}
                  onClick={onViewTeams}
                >
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>▶</span>
                    {t.name}
                  </div>
                  <div style={{ display: 'flex', gap: 4, paddingLeft: 16 }}>
                    {teamAgents.slice(0, 5).map((a) => (
                      <div key={a.id} style={{ position: 'relative' }}>
                        <div style={{
                          width: 22, height: 22,
                          borderRadius: '50%',
                          background: '#4F46E5',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 600, color: 'white',
                        }}>
                          {a.displayName.slice(0, 2).toUpperCase()}
                        </div>
                        <div style={{ position: 'absolute', bottom: -1, right: -1 }}>
                          <StatusDot status={a.status} size={6} />
                        </div>
                      </div>
                    ))}
                    {teamAgents.length === 0 && (
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No agents</span>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '8px 0', flexShrink: 0 }}>
        <SidebarNavItem
          icon="⚙️"
          label="Settings"
          active={activeView === 'settings'}
          onClick={onViewSettings}
        />
      </div>
    </div>
  );
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '10px 16px 4px',
      fontSize: 11,
      fontWeight: 600,
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}>
      {children}
    </div>
  );
}

function SidebarNavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 'calc(100% - 12px)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 16px',
        background: active ? 'white' : 'transparent',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        color: active ? 'var(--text)' : 'var(--text-secondary)',
        fontWeight: active ? 500 : 400,
        fontSize: 13,
        borderRadius: 6,
        margin: '1px 6px',
        boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
        transition: 'all 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.05)';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      <span>{icon}</span>
      <span>{label}</span>
    </button>
  );
}
